import { useRef } from 'react';
import { toastrError } from '@/shared/lib/toastNotification';
import type { HintCommentsProps } from './index';

/**
 * Backs the technician comments hint field with its length validation. Tracks validity in a ref, fires a single error toast the first time the text passes 520 characters, and forwards the field name, value and validity to the parent's updateComments callback.
 *
 * @param updateComments - Callback receiving the field name, its value and whether it is within the 520 character limit.
 * @returns An object carrying the onChange handler for the comments field.
 */
const useHintComments = ({ updateComments }: Pick<HintCommentsProps, 'updateComments'>) => {
  const isValidComments = useRef(false);

  /**
   * Validates a comment field edit and reports it upward. Fires a single error toast when the value exceeds 520 characters and marks the field invalid, marks it valid otherwise, then calls updateComments with the name, value and validity.
   *
   * @param name - Name of the comment field being edited.
   * @param value - New text entered in the field.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string) => {
    if (value.length > 520) {
      if (isValidComments.current) {
        toastrError('Exceeded maximum of 520 characters in Technician Comments');
        isValidComments.current = false;
      }
    } else {
      isValidComments.current = true;
    }
    updateComments(name, value, isValidComments.current);
  };

  return { onChange };
};

export default useHintComments;
